import { gql, useApolloClient, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  START_SURVEILLANCE_ROUTE,
  FINISH_SURVEILLANCE_ROUTE,
  CREATE_ROUTE_SEGMENT,
  DELETE_ROUTE_SEGMENT,
  FINISH_ROUTE_SEGMENT,
  UPDATE_SEGMENT_PAUSE_CAUSE,
  CORRECT_ROUTE_SEGMENT,
  UPDATE_ROUTE_SEGMENT,
  LINK_ORDER_TO_SEGMENT,
  LINK_EXTERNAL_ORDER_TO_SEGMENT,
  UNLINK_ORDER_FROM_SEGMENT,
  LINK_ORDER_TO_SURVEILLANCE_ROUTE,
  UNLINK_ORDER_FROM_SURVEILLANCE_ROUTE,
  ADD_GPS_POINTS_BATCH,
  SPLIT_ROUTE_SEGMENT,
  UNDO_SEGMENT_SPLIT,
  TRANSFER_ROUTE_SEGMENT,
  MOVE_ROUTE_SEGMENTS,
} from '../gql/mutations/surveillanceRoute';
import {
  SurveillanceRoute,
  RouteRoadStretch,
  RouteSegment,
  NewSurveillanceRoute,
  SegmentStopInput,
  SplitRouteSegmentResult,
  TransferRouteSegmentResult,
  MoveRouteSegmentsInput,
  MoveRouteSegmentsResult,
} from '../types/surveillanceRoute';
import {
  showBackendErrorToast,
  showErrorToast,
  showSuccessToast,
} from '../utils/toasts';
import { markSurveillanceRouteListStale } from '../gql/surveillanceRouteListFreshness';
import { isOfflineQueuedError } from '../offline/isOfflineQueuedError';
import { isLegacyNetworkFailure } from '../offline/transportFailure';
import { warmupOrdersByIds } from '../gql/selectors/warmupOrders';
import {
  addRoadsToSurveillanceRouteCache,
  evictAfterSegmentEdit,
  stampRouteLastSegmentEnd,
} from '../gql/cacheHelpers';
import {
  isNoConnectionError,
  MOVE_SEGMENTS_ERROR_TOAST,
  orderIdsToEvictAfterMove,
} from '../utils/moveSegments';

// ═══════════════════════════════════════════════════════════════════
// TYPES — Timer iteration 2026-04-15
// ═══════════════════════════════════════════════════════════════════

// BUG contrato start/finish (2026-07-12, cazado en QA IA): el backend
// renombró a los canónicos `registerInitDate`/`registerEndDate` (V4
// post-refactor 2026-04-22) pero este hook seguía mandando
// `actualStartDate`/`actualEndDate` — startSurveillanceRoute reventaba con
// "Field registerInitDate of required type Date! was not provided" y el RV
// no arrancaba nunca desde el botón Iniciar de la cabecera.
interface StartRouteInput {
  id: string;
  registerInitDate: string;
}

interface FinishRouteInput {
  id: string;
  registerEndDate?: string;
}

export interface GpsPointInput {
  latitude: number;
  longitude: number;
  timestamp: string;
  speed?: number;
  accuracy?: number;
}

/**
 * Input para crear un nuevo timer (RouteSegment).
 * Solo PK de inicio + dirección — el PK de cierre se rellena al pausar/finalizar.
 */
interface CreateSegmentInput {
  surveillanceRouteId: string;
  /**
   * Multi-carretera (2026-08-15): en qué carretera del recorrido arranca el
   * tramo. Ausente = la primera, que es lo de siempre. Este tipo es el que
   * de verdad guarda la puerta (los cuatro callers pasan por aquí): mientras
   * no tuvo el campo, el `roadId` que el operario elegía en el modal se caía
   * por el camino sin que nada avisara.
   */
  roadId?: string;
  direction: string;
  directionName?: string;
  lane?: string;
  pkInitKm: number;
  pkInitMeter: number;
  initLatitude?: number;
  initLongitude?: number;
  // SIN `description` (2026-08-10): un tramo NO se describe al nacer, se
  // describe al cerrarse (`finishSegment.pauseCauseDetail`). El campo sigue
  // existiendo en el input `NewRouteSegment` del backend a propósito, para que
  // una operación encolada OFFLINE antes de este cambio se reproduzca sin
  // romper; pero desde el frontend ya no lo escribe nadie y quitarlo de este
  // tipo hace que el compilador impida reintroducirlo por descuido.
  // Hora de inicio capturada en el CLIENTE (ISO). Opcional: si el caller no
  // la pasa, `createSegment` la sella con `new Date()` en el momento de la
  // llamada. Ver JSDoc de createSegment (offline-safe).
  registerInitDate?: string;
}

/**
 * Input para cerrar un timer (con o sin pausa).
 * Si pauseCause != null el recorrido transiciona a 'paused'.
 */
interface FinishSegmentInput {
  id: string;
  endDate?: string;
  pkEndKm: number;
  pkEndMeter: number;
  endLatitude?: number;
  endLongitude?: number;
  pauseCause?: string;
  pauseCauseDetail?: string;
  // D1 (2026-07-23): carril del tramo que se cierra (antes se pedía al iniciar).
  lane?: string;
  /**
   * Por qué carreteras pasó el tramo (multi-carretera 2026-08-17). Lo calcula
   * el modal de cierre del rastro GPS ya grabado. Ausente = no tocar lo
   * guardado: el cierre interno del recorrido y los replays de la cola no lo
   * mandan, y borrárselo sería perder el dato en silencio.
   */
  roadStretches?: RouteRoadStretch[];
}

// ═══════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════

/**
 * Queries a revalidar tras las mutaciones del RV.
 *
 * 🔴 `refetchQueries` POR NOMBRE solo alcanza a las queries que Apollo tiene
 * como observable VIVO en ese instante — un `useQuery` montado y no saltado
 * (`QueryManager.getObservableQueries`: descarta `fetchPolicy === 'standby'`,
 * que es lo que pone `skip`, y las que no tienen observadores). Lo que se pide
 * desde un loader con `client.query` NUNCA es observable: coge el dato y se va.
 *
 * Medido en el navegador (2026-09-07, Apollo 3.14) llamando a
 * `client.refetchQueries` en el detalle del recorrido con los cinco que había:
 *
 *   pedidas: 5  →  avisos: 3  →  peticiones de red: 2
 *
 * Los retirados no producían tráfico ninguno, solo el aviso «Unknown query
 * named X requested in refetchQueries options.include array» en CADA mutación
 * (los tres que se veían en consola al iniciar un recorrido):
 *
 *   - `GetSurveillanceRoutes` → NO REGISTRADA ni estando en su propia lista
 *     (su loader es cache-read-first). Su refresco lo hace ahora el marcador de
 *     caducidad, ver `invalidateRouteList` más abajo.
 *   - `GetRouteSegments`      → NO REGISTRADA; el loader del detalle ya la pide
 *     con `network-only` y el `revalidator` lo reejecuta.
 *   - `GetSurveillanceRouteById` → el caso menos evidente: FUNCIONA en el
 *     detalle (`Breadcrumbs` la monta), pero ahí es una petición DUPLICADA —
 *     medido, el loader del detalle la pide igualmente con `network-only` en
 *     cada `revalidate()`, y el `useQuery` de Breadcrumbs recibe ese dato por
 *     la difusión de la caché. Fuera del detalle está en `standby` y solo
 *     dejaba el aviso. Es decir: donde sirve, sobra; donde no sirve, molesta.
 */
const REFETCH = [
  // Observable vivo en TODA la app: el `TimerOverlay` va en el `Header`, así que
  // su lista de recorridos activos (el campo `activeSurveillanceRoutes`,
  // distinto de `surveillanceRoutes`) siempre está montada. Sin esto, al
  // FINALIZAR un recorrido seguía saliendo en el desplegable del cronómetro
  // hasta que caía el sondeo de 30 s.
  'GetActiveSurveillanceRoutes',
  /**
   * SE QUEDA, aunque sea el que más avisos deja. Estuvo fuera un rato y era una
   * REGRESIÓN de FR-10.5 (ronda 4).
   *
   * Verificado en el navegador: el `skip: !selectedRouteId` del TimerOverlay la
   * deja en `standby` mientras no hay recorrido elegido —y ahí es donde salta el
   * aviso—, pero en cuanto el operario elige uno pasa a `cache-and-network` con
   * observadores, que es EXACTAMENTE el momento en que se opera el cronómetro y
   * se disparan `createSegment`/`finishSegment`.
   *
   * Y no lo cubre nadie más: `activeRouteSegment` es un campo RAÍZ parametrizado
   * por recorrido, así que crear un tramo no lo actualiza por normalización (el
   * cliente no puede saber que el tramo nuevo es ahora «el activo»), y ningún
   * loader lo pide — no lo alcanza el `revalidator`. Sin este nombre, el
   * cronómetro tardaría hasta 30 s (el sondeo) en reflejar iniciar o cerrar un
   * tramo, que es el bug que FR-10.5 vino a arreglar.
   *
   * O sea: el aviso solo aparece cuando no hay nada que refrescar. Se prefiere
   * ese ruido a devolver la espera de 30 s.
   */
  'GetActiveRouteSegment',
];

/** Las Orders de un tramo que una pantalla puede tener normalizadas. */
const SEGMENT_ORDER_REFS = gql`
  fragment SegmentOrderRefs on RouteSegment {
    orderIDs
    linkedBasicActionOrder {
      id
    }
  }
`;

export function useSurveillanceRoutes() {
  const { t } = useTranslation(['toasts', 'common']);
  const client = useApolloClient();

  /**
   * La gemela y la básica de un tramo según la caché, leídas ANTES de la
   * mutación (después la básica ya puede estar soltada). Sin el tramo en
   * caché, ninguna: lo demás lo cubre `evictAfterSegmentEdit`.
   */
  const segmentOrderIds = (segmentId: string): string[] => {
    try {
      const segment = client.cache.readFragment<{
        orderIDs?: string[] | null;
        linkedBasicActionOrder?: { id: string } | null;
      }>({
        id: client.cache.identify({
          __typename: 'RouteSegment',
          id: segmentId,
        }),
        fragment: SEGMENT_ORDER_REFS,
      });
      return [
        ...(segment?.orderIDs ?? []),
        ...(segment?.linkedBasicActionOrder?.id
          ? [segment.linkedBasicActionOrder.id]
          : []),
      ];
    } catch {
      return [];
    }
  };

  const toastSuccess = (key: string) => showSuccessToast(t(key));
  // FUENTE ÚNICA del toast de error de estas mutaciones (2026-07-17):
  // prefiere el mensaje de negocio del backend (ConflictException etc.,
  // p.ej. "El usuario ya tiene un timer activo en otro recorrido de
  // vigilancia") y cae al genérico i18n. Los callers (useRouteTimerActions,
  // TimerOverlay) NO deben tostear también — aquí ya se emite uno.
  const toastError = (err: unknown, fallbackKey = 'toasts:error.generic') =>
    showBackendErrorToast(err, t(fallbackKey));
  // Las herramientas de oficina (partir, deshacer, transferir) no se encolan:
  // sin red fallan a la vista. Que el aviso diga eso y no «Ha ocurrido un
  // error» (QA 15-oct, T1.11): el modal sigue abierto para repetirlo con red.
  const toastOfficeError = (err: unknown) =>
    isLegacyNetworkFailure(err)
      ? showErrorToast(t('common:offline.noConnection'))
      : toastError(err);

  /**
   * Tras partir, deshacer, corregir o transferir (QA 15-oct, T2.2+listado):
   * la «Fecha final» del listado de recorridos sale de la Order GEMELA de
   * cada recorrido, y el listado la lee SOLO de la caché (persistida:
   * sobrevive a F5). Sacarla no basta —la fila saldría sin fecha hasta la
   * próxima visita—: se vuelve a pedir ya. Va después de
   * `evictAfterSegmentEdit`, que ya la sacó. Fuego y olvido: el warm-up nunca
   * lanza.
   */
  const rereadRouteOrders = (ids: Array<string | null | undefined>) => {
    const wanted = ids.filter((id): id is string => Boolean(id));
    if (wanted.length > 0) {
      void warmupOrdersByIds(wanted, false, 'network-only');
    }
  };

  // Encolado offline = ÉXITO diferido (plan sr-offline-retry-parity T4): el
  // OfflineQueueLink ya persistió la op y se reproducirá al reconectar. El
  // usuario ve el mismo aviso que en ChangeStateModal y el caller recibe
  // `null` (sentinel "queued") para seguir su flujo sin tratarlo como fallo.
  const toastQueued = () =>
    showSuccessToast(t('modals:changeState.queuedOffline'));

  /**
   * Marca la tabla de recorridos como CADUCADA (no la borra).
   *
   * Su loader es cache-read-first —solo va al servidor si la caché está vacía—,
   * así que ni `revalidate()` la refresca (reejecuta el loader, que vuelve a
   * leer su caché: medido, 0 peticiones) ni `refetchQueries` por nombre puede
   * (esa query nunca es un observable). Hay que decírselo de otra forma.
   *
   * 🔴 Marcar, no borrar. Borrar la caché funcionaba con red, pero medido en el
   * navegador sin cobertura dejaba la tabla en 0 filas donde antes enseñaba 26 —
   * y el operario de un RV trabaja justo donde la cobertura se cae. El motivo
   * completo y las alternativas descartadas de Apollo están en
   * `gql/surveillanceRouteListFreshness.ts`.
   *
   * Se llama solo cuando la mutación SALIÓ BIEN: si se encoló offline no ha
   * cambiado nada en el servidor y no hay nada que revalidar.
   *
   * Hace falta porque el camino del cronómetro (`useRouteTimerActions`,
   * `TimerOverlay`) no refrescaba NADA de la lista — verificado: cero
   * referencias a `evictDerivedCaches` ni a `cache.evict` en esos ficheros.
   */
  const invalidateRouteList = () => markSurveillanceRouteListStale();

  const [startRouteMutation, { loading: isStarting }] = useMutation(
    START_SURVEILLANCE_ROUTE,
    { refetchQueries: REFETCH }
  );

  const [finishRouteMutation, { loading: isFinishing }] = useMutation(
    FINISH_SURVEILLANCE_ROUTE,
    { refetchQueries: REFETCH }
  );

  const [createSegmentMutation, { loading: isCreatingSegment }] = useMutation(
    CREATE_ROUTE_SEGMENT,
    { refetchQueries: REFETCH }
  );

  const [deleteSegmentMutation, { loading: isDeletingSegment }] = useMutation(
    DELETE_ROUTE_SEGMENT,
    { refetchQueries: REFETCH }
  );

  const [finishSegmentMutation, { loading: isFinishingSegment }] = useMutation(
    FINISH_ROUTE_SEGMENT,
    { refetchQueries: REFETCH }
  );

  const [updatePauseCauseMutation, { loading: isUpdatingPauseCause }] =
    useMutation(UPDATE_SEGMENT_PAUSE_CAUSE, { refetchQueries: REFETCH });
  const [correctSegmentMutation, { loading: isCorrectingSegment }] =
    useMutation(CORRECT_ROUTE_SEGMENT, { refetchQueries: REFETCH });
  // «Registrar parada» (2026-10-06): sin refetchQueries — la página revalida
  // su loader, que es lo que pinta la tabla (como en «Corregir»).
  const [splitSegmentMutation, { loading: isSplittingSegment }] =
    useMutation(SPLIT_ROUTE_SEGMENT);
  const [undoSegmentSplitMutation, { loading: isUndoingSegmentSplit }] =
    useMutation(UNDO_SEGMENT_SPLIT);
  const [transferSegmentMutation, { loading: isTransferringSegment }] =
    useMutation(TRANSFER_ROUTE_SEGMENT);
  const [moveSegmentsMutation, { loading: isMovingSegments }] =
    useMutation(MOVE_ROUTE_SEGMENTS);
  const [updateSegmentFieldsMutation] = useMutation(UPDATE_ROUTE_SEGMENT, {
    refetchQueries: REFETCH,
  });

  // V4 D2 (2026-05-07) — cross-link mutations
  const [linkOrderMutation, { loading: isLinkingOrder }] = useMutation(
    LINK_ORDER_TO_SEGMENT,
    { refetchQueries: REFETCH }
  );
  // Cross-link 3 (2026-09-22): recorrido ↔ OT. La mutation devuelve el
  // recorrido con `linkedExternalOrders`, así que el detalle se refresca por
  // id; el REFETCH cubre al cronómetro como en el resto.
  const [linkRouteOrderMutation] = useMutation(
    LINK_ORDER_TO_SURVEILLANCE_ROUTE,
    { refetchQueries: REFETCH }
  );
  const [unlinkRouteOrderMutation] = useMutation(
    UNLINK_ORDER_FROM_SURVEILLANCE_ROUTE,
    { refetchQueries: REFETCH }
  );

  const [linkExternalMutation, { loading: isLinkingExternal }] = useMutation(
    LINK_EXTERNAL_ORDER_TO_SEGMENT,
    { refetchQueries: REFETCH }
  );
  const [unlinkOrderMutation, { loading: isUnlinking }] = useMutation(
    UNLINK_ORDER_FROM_SEGMENT,
    { refetchQueries: REFETCH }
  );

  const [addGpsPointsBatchMutation] = useMutation(ADD_GPS_POINTS_BATCH);

  // ═══════════════════════════════════════════════════════════════════
  // ROUTE CRUD
  // ═══════════════════════════════════════════════════════════════════

  // ═══════════════════════════════════════════════════════════════════
  // ROUTE CONTROL — Timer iteration 2026-04-15
  // pauseRoute / resumeRoute eliminados. Las operaciones equivalentes son:
  //   - "Pausar timer" → finishSegment({ pauseCause: 'xxx' })
  //   - "Reanudar timer" → createSegment (nuevo timer)
  // ═══════════════════════════════════════════════════════════════════

  const startRoute = async (
    input: StartRouteInput,
    // silentSuccess: las cadenas (iniciar recorrido→tramo) emiten UN toast.
    opts?: { silentSuccess?: boolean }
  ): Promise<SurveillanceRoute | null> => {
    try {
      const result = await startRouteMutation({ variables: { input } });
      // El recorrido pasa de `open` a `in-progress`: cambia su estado en la
      // tabla y, con un filtro de estado puesto, también si debe seguir en ella.
      invalidateRouteList();
      if (!opts?.silentSuccess) toastSuccess('toasts:success.updated');
      return result.data?.startSurveillanceRoute;
    } catch (err) {
      if (isOfflineQueuedError(err)) {
        if (!opts?.silentSuccess) toastQueued();
        return null;
      }
      toastError(err);
      throw err;
    }
  };

  const finishRoute = async (
    input: FinishRouteInput,
    opts?: { silentSuccess?: boolean }
  ): Promise<SurveillanceRoute | null> => {
    try {
      // Hora de FIN del recorrido sellada en el CLIENTE (offline-safe), igual
      // que registerInitDate/endDate de create/finishSegment. `handleFinishRoute`
      // ya la pasa; inyectarla aquí cubre a cualquier otro caller y evita que
      // un finish de ruta encolado sin red quede con la hora de reconexión
      // (el backend hace `input.registerEndDate ?? now`).
      const registerEndDate = input.registerEndDate ?? new Date().toISOString();
      const result = await finishRouteMutation({
        variables: { input: { ...input, registerEndDate } },
      });
      // El caso que motivó todo esto: con el filtro «En curso» puesto, un
      // recorrido finalizado desde el cronómetro se quedaba en la tabla.
      invalidateRouteList();
      if (!opts?.silentSuccess) toastSuccess('toasts:success.updated');
      return result.data?.finishSurveillanceRoute;
    } catch (err) {
      if (isOfflineQueuedError(err)) {
        if (!opts?.silentSuccess) toastQueued();
        return null;
      }
      toastError(err);
      throw err;
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // TIMER (RouteSegment) METHODS
  // ═══════════════════════════════════════════════════════════════════

  /**
   * La vía de un tramo pasa a ser del recorrido EN LA TABLET (2026-09-23).
   *
   * Si el tramo se abre o se cierra en una vía que el recorrido no tenía —la
   * patrulla apoyando al otro tramo— el servidor la añade; esto hace lo mismo
   * en la caché para que el cronómetro la trate como suya ya, con red o
   * encolado. Sin vía ajena no hace nada (`addRoadsToSurveillanceRouteCache`
   * no duplica).
   */
  const adoptSegmentRoads = (
    routeId: string | undefined,
    roadIds: Array<string | null | undefined>
  ) => {
    if (!routeId) return;
    addRoadsToSurveillanceRouteCache(
      client.cache,
      routeId,
      roadIds.filter((id): id is string => !!id)
    );
  };

  /**
   * Revisión W5 (M7): el fin del último tramo del recorrido, ya en la caché,
   * para que la pregunta «¿sigues el turno…?» (P1) no salte sobre el propio
   * turno tras un reinicio sin cobertura. Ver `stampRouteLastSegmentEnd`.
   */
  const stampLastSegmentEnd = (routeId: string | undefined, endDate: string) => {
    if (!routeId) return;
    stampRouteLastSegmentEnd(client.cache, routeId, endDate);
  };

  const createSegment = async (
    input: CreateSegmentInput,
    // silentSuccess: el cambio de sentido encadena finish+create y para el
    // usuario es UNA operación → el caller silencia los éxitos individuales
    // y emite un único toast. Los ERRORES siempre suenan.
    opts?: { silentSuccess?: boolean }
  ): Promise<RouteSegment | null> => {
    try {
      // Hora de INICIO capturada AQUÍ, en el cliente (offline-safe). Sin esto,
      // el backend estampaba `startDate = now()` del SERVIDOR: un tramo creado
      // offline (iniciar recorrido / cambio de sentido) se reproduce al
      // reconectar y quedaba con la hora de RECONEXIÓN, no la del campo →
      // timeline, duraciones y la ventana GPS del tramo activo corruptas.
      // Espejo de `startRoute` (registerInitDate) y `finishSegment` (endDate).
      // Se sella en el momento de la llamada (que es cuando el usuario confirma
      // el modal), así todos los callers (start + cambio de sentido en
      // TimerOverlay) quedan cubiertos sin tocarlos.
      const registerInitDate =
        input.registerInitDate ?? new Date().toISOString();
      // Idempotencia: el replay offline de "iniciar/reanudar timer" reutiliza
      // el mismo clientOpId → el backend devuelve el timer ya creado en vez de
      // duplicarlo o chocar con el guard 'ya hay un timer activo'.
      const result = await createSegmentMutation({
        variables: {
          input: {
            ...input,
            registerInitDate,
            clientOpId: crypto.randomUUID(),
          },
        },
      });
      // El backend auto-transiciona el RECORRIDO al abrir/cerrar un tramo
      // (`syncStateFromSegments`), así que su fila de la tabla cambia de estado
      // aunque la mutación sea de tramo.
      invalidateRouteList();
      adoptSegmentRoads(input.surveillanceRouteId, [input.roadId]);
      if (!opts?.silentSuccess) toastSuccess('toasts:success.created');
      return result.data?.createRouteSegment;
    } catch (err) {
      if (isOfflineQueuedError(err)) {
        adoptSegmentRoads(input.surveillanceRouteId, [input.roadId]);
        if (!opts?.silentSuccess) toastQueued();
        return null;
      }
      toastError(err);
      throw err;
    }
  };

  // `updateSegment` (UPDATE_ROUTE_SEGMENT) eliminado 2026-07-21: export
  // muerto sin ningún consumidor UI — la edición de campos del tramo viaja
  // hoy por createSegment/finishSegment/updateSegmentPauseCause.

  const deleteSegment = async (id: string): Promise<{ id: string } | null> => {
    try {
      const result = await deleteSegmentMutation({ variables: { id } });
      // Borrar un tramo entra por el mismo sitio que crearlo y cerrarlo: el
      // `delete` del backend llama a `syncStateFromSegments`, así que puede
      // devolver el recorrido a `open` si era el único tramo.
      //
      // ⚠️ Hoy es DEFENSIVO: `deleteSegment` se exporta pero no lo llama nadie
      // (0 consumidores, y la tabla de tramos no tiene botón de borrar), así
      // que esta línea no se ejecuta en la app actual — no está validada en
      // navegador como las otras cuatro. Se pone para que el día que se cablee
      // el botón no repita el fallo que arregla esta rama.
      invalidateRouteList();
      toastSuccess('toasts:success.deleted');
      return result.data?.deleteRouteSegment;
    } catch (err) {
      if (isOfflineQueuedError(err)) {
        toastQueued();
        return null;
      }
      toastError(err, 'toasts:error.deleteFailed');
      throw err;
    }
  };

  /**
   * Cierra el timer activo. Llamar con `pauseCause` para una pausa operativa
   * (el recorrido pasa a 'paused'); sin pauseCause es un cierre explícito
   * (lo usa internamente finishRoute).
   */
  const finishSegment = async (
    input: FinishSegmentInput,
    // Ver createSegment: solo silencia el toast de ÉXITO (cambio de sentido).
    // `routeId`: el recorrido del tramo, para adoptar en la caché la vía de
    // apoyo por la que acaben sus trozos. Va aquí y no en `input` porque ese
    // viaja entero al servidor, y su esquema no lo conoce.
    opts?: { silentSuccess?: boolean; routeId?: string }
  ): Promise<RouteSegment | null> => {
    // Hora de FIN capturada AQUÍ, en el cliente (offline-safe). Espejo del
    // registerInitDate de createSegment: sin esto, el finish de un CAMBIO DE
    // SENTIDO (TimerOverlay no pasa endDate) se encolaba offline y el backend
    // estampaba `endDate = now()` del SERVIDOR al reproducirlo → hora de
    // RECONEXIÓN, no la de cuando el operario pulsó terminar. `handleFinishRoute`
    // ya pasa su propio endDate, así que respetamos el del caller si viene.
    // (Fuera del try: el camino encolado también la necesita, revisión W5 M7.)
    const endDate = input.endDate ?? new Date().toISOString();
    try {
      const result = await finishSegmentMutation({
        variables: { input: { ...input, endDate } },
      });
      // Espejo de `createSegment`: cerrar el tramo también mueve el estado del
      // recorrido en el servidor.
      invalidateRouteList();
      adoptSegmentRoads(
        opts?.routeId,
        (input.roadStretches ?? []).map((stretch) => stretch.roadId)
      );
      stampLastSegmentEnd(opts?.routeId, endDate);
      if (!opts?.silentSuccess) toastSuccess('toasts:success.updated');
      return result.data?.finishRouteSegment;
    } catch (err) {
      if (isOfflineQueuedError(err)) {
        adoptSegmentRoads(
          opts?.routeId,
          (input.roadStretches ?? []).map((stretch) => stretch.roadId)
        );
        // Revisión W5 (M7): SIN RED es donde importa — la caché es lo único
        // que la tablet sabe del turno hasta sincronizar.
        stampLastSegmentEnd(opts?.routeId, endDate);
        if (!opts?.silentSuccess) toastQueued();
        return null;
      }
      toastError(err);
      throw err;
    }
  };

  /**
   * V4 D6: cambia el `pauseCause` de un tramo cerrado. El backend reconcilia
   * los vínculos cross-link automáticamente (e.g. si pasa de `action` a
   * `external-call` desvincula la actuación previa y marca pendingExternalLink).
   */
  const updateSegmentPauseCause = async (
    input: {
      segmentId: string;
      pauseCause: string;
      pauseCauseDetail?: string;
      /**
       * Las carreteras por las que fue el tramo, cuando gestión las corrige a
       * mano (mc53). Ausente = no tocar.
       *
       * Se declara aquí aunque el modal ya la tipe en su payload: al pasar una
       * variable, TypeScript NO comprueba las propiedades de más, así que sin
       * esta línea el campo viajaba sin que nadie lo hubiera declarado en el
       * camino — y una errata en el nombre no se habría visto hasta el navegador.
       */
      roadIds?: string[];
    },
    /**
     * `silentSuccess`: sin el aviso genérico de éxito, para quien avisa con uno
     * propio (al pasar a «Aviso»: a qué OT se ligó). El «en cola» y los errores
     * se avisan siempre.
     */
    options: { silentSuccess?: boolean } = {}
  ): Promise<RouteSegment | null> => {
    try {
      const result = await updatePauseCauseMutation({ variables: { input } });
      if (!options.silentSuccess) toastSuccess('toasts:success.updated');
      return result.data?.updateSegmentPauseCause;
    } catch (err) {
      if (isOfflineQueuedError(err)) {
        toastQueued();
        return null;
      }
      toastError(err);
      throw err;
    }
  };

  /**
   * Corrección de gestión de un tramo CERRADO (spec 2026-07-31): PK inicial,
   * PK final, sentido, carril y horas. Lo que no se envía no se toca.
   *
   * El backend valida el solapamiento contra los tramos vecinos en el tiempo y
   * exige el recorrido pausado o pendiente de validación; sus códigos
   * `SR_SEGMENT_*` los traduce `toastError` vía `errors:backend.<código>`.
   * Se re-lanza el error para que el modal siga abierto y se pueda corregir.
   */
  const correctSegment = async (input: {
    id: string;
    pkInitKm?: number;
    pkInitMeter?: number;
    pkEndKm?: number;
    pkEndMeter?: number;
    direction?: string;
    directionName?: string;
    lane?: string;
    startDate?: string;
    endDate?: string;
    /**
     * Descripción del tramo (2026-08-10) = `pauseCauseDetail`. Ausente = no
     * tocar; cadena vacía = borrarla. La CAUSA no se cambia por aquí — para
     * eso está `updateSegmentPauseCause`, que además dispara la cadena de la
     * Actuación básica cuando pasa a `action`.
     */
    pauseCauseDetail?: string;
  },
  /** La gemela del recorrido: sus fechas pueden cambiar (fecha final). */
  routeOrderId?: string | null
  ): Promise<RouteSegment | null> => {
    try {
      const before = segmentOrderIds(input.id);
      const result = await correctSegmentMutation({ variables: { input } });
      // Fechas y PK del tramo cambian horas, fecha final e informes (D1).
      evictAfterSegmentEdit(client.cache, [
        ...before,
        ...(result.data?.correctRouteSegment?.orderIDs ?? []),
        routeOrderId,
      ]);
      rereadRouteOrders([routeOrderId]);
      toastSuccess('toasts:success.updated');
      return result.data?.correctRouteSegment ?? null;
    } catch (err) {
      if (isOfflineQueuedError(err)) {
        toastQueued();
        return null;
      }
      toastError(err);
      throw err;
    }
  };

  /**
   * «Registrar parada» (spec 2026-10-06): parte un tramo CERRADO por las
   * paradas que la patrulla no pausó. Herramienta de oficina:
   *
   *   - NO entra en la cola offline (`skipOfflineQueue`): sin red no hay GPS
   *     crudo ni vecinos frescos que validar, y encolarla la reproduciría a
   *     ciegas más tarde;
   *   - lleva `clientOpId`: si el RetryLink la reenvía, el servidor devuelve
   *     las mismas piezas en vez de partir dos veces.
   *
   * El toast de éxito lo pone la página, porque depende de qué pasó con el
   * aviso (ligado, pendiente o ambiguo). El error sí suena aquí y se re-lanza
   * para que el modal siga abierto.
   */
  const splitSegment = async (
    segmentId: string,
    stops: SegmentStopInput[],
    /** La gemela del recorrido: sus fechas pueden cambiar (fecha final). */
    routeOrderId?: string | null
  ): Promise<SplitRouteSegmentResult | null> => {
    try {
      const before = segmentOrderIds(segmentId);
      const result = await splitSegmentMutation({
        variables: {
          input: { segmentId, clientOpId: crypto.randomUUID(), stops },
        },
        context: { skipOfflineQueue: true },
      });
      evictAfterSegmentEdit(client.cache, [
        ...before,
        ...(result.data?.splitRouteSegment?.segments ?? []).flatMap(
          (segment: RouteSegment) => segment.orderIDs ?? []
        ),
        routeOrderId,
      ]);
      rereadRouteOrders([routeOrderId]);
      return result.data?.splitRouteSegment ?? null;
    } catch (err) {
      toastOfficeError(err);
      throw err;
    }
  };

  /**
   * Deshace la última parada registrada en un tramo. Misma doctrina; el toast
   * de éxito también lo pone la página.
   */
  const undoSegmentSplit = async (
    segmentId: string,
    /** La gemela del recorrido: sus fechas pueden cambiar (fecha final). */
    routeOrderId?: string | null
  ): Promise<boolean> => {
    try {
      const before = segmentOrderIds(segmentId);
      await undoSegmentSplitMutation({
        variables: { segmentId },
        context: { skipOfflineQueue: true },
      });
      evictAfterSegmentEdit(client.cache, [...before, routeOrderId]);
      rereadRouteOrders([routeOrderId]);
      return true;
    } catch (err) {
      toastOfficeError(err);
      throw err;
    }
  };

  /**
   * «Transferir tramo» (spec 2026-10-07): mueve un tramo cerrado a otro
   * recorrido. Gesto de gestión, fuera de la cola offline (un rechazo al
   * reproducirlo se descartaría en silencio). El `clientOpId` lo pone quien
   * llama, uno por intento del usuario: un reintento del mismo modal no
   * transfiere dos veces. El toast de éxito lo pone la página.
   */
  const transferSegment = async (
    segmentId: string,
    targetRouteId: string,
    clientOpId: string,
    /**
     * El recorrido donde estaba el tramo al abrir el modal: si otro lo ha
     * movido entre medias, el servidor responde SR_TRANSFER_SOURCE_CHANGED
     * en vez de moverlo desde donde esté ahora (QA 15-oct, T2.10).
     */
    sourceRouteId?: string
  ): Promise<TransferRouteSegmentResult | null> => {
    try {
      const before = segmentOrderIds(segmentId);
      const result = await transferSegmentMutation({
        variables: {
          input: { segmentId, targetRouteId, clientOpId, sourceRouteId },
        },
        context: { skipOfflineQueue: true },
      });
      // Cambian las horas y las fechas de los dos recorridos, y el ROID del
      // tramo; las básicas soltadas pierden su vínculo (D1).
      const transferred = result.data?.transferRouteSegment;
      const routeOrderIds = transferred?.routeOrderIds ?? [];
      evictAfterSegmentEdit(client.cache, [
        ...before,
        ...(transferred?.segment?.orderIDs ?? []),
        ...(transferred?.unlinkedBasicOrderIds ?? []),
        ...routeOrderIds,
      ]);
      // Las gemelas de los DOS recorridos: de ahí sale la «Fecha final».
      rereadRouteOrders(routeOrderIds);
      return transferred ?? null;
    } catch (err) {
      toastOfficeError(err);
      throw err;
    }
  };

  /**
   * «Mover tramos» (plan 2026-10-15 § 3): un rango de tramos a otro
   * recorrido, existente o nuevo. Misma doctrina que transferir: fuera de la
   * cola offline (`skipOfflineQueue`: un rechazo al reproducirlo se
   * descartaría en silencio) y con el `clientOpId` de quien llama, uno por
   * apertura del modal. Cambian las horas, las fechas y los ROID de los
   * tramos y de sus básicas: se vacían las cachés derivadas como al
   * transferir. El toast de éxito lo pone la página.
   */
  const moveSegments = async (
    input: MoveRouteSegmentsInput
  ): Promise<MoveRouteSegmentsResult | null> => {
    try {
      const before = input.segmentIds.flatMap(segmentOrderIds);
      const result = await moveSegmentsMutation({
        variables: { input },
        context: { skipOfflineQueue: true },
      });
      const moved: MoveRouteSegmentsResult | null =
        result.data?.moveRouteSegments ?? null;
      evictAfterSegmentEdit(
        client.cache,
        orderIdsToEvictAfterMove(before, moved)
      );
      return moved;
    } catch (err) {
      // QA 15-oct: sin red, decirlo («Sin conexión»), y un solo aviso aunque
      // salgan dos peticiones en el mismo instante.
      if (isNoConnectionError(err)) {
        showErrorToast(t('common:offline.noConnection'), {
          toastId: MOVE_SEGMENTS_ERROR_TOAST,
        });
      } else {
        showBackendErrorToast(err, t('toasts:error.generic'), {
          toastId: MOVE_SEGMENTS_ERROR_TOAST,
        });
      }
      throw err;
    }
  };

  /**
   * Corrección del OPERARIO (spec 2026-08-15): sentido, carril y descripción de
   * un tramo cerrado de un recorrido de su equipo. Lo que no se envía no se
   * toca; `pauseCauseDetail: ''` borra la descripción.
   *
   * Camino SEPARADO de `correctSegment`, que es el de gestión y toca además PK
   * y horas. El backend se encarga de que esta puerta sea Tablet-only; aquí no
   * se decide nada de permisos.
   *
   * Sin cobertura la mutación se encola sola (`OfflineQueueLink` no tiene
   * allowlist) y el replay es inofensivo: fija valores absolutos, así que
   * repetirlo da el mismo resultado y no necesita `clientOpId`.
   */
  const updateSegmentFields = async (input: {
    id: string;
    direction?: string;
    directionName?: string;
    lane?: string;
    pauseCauseDetail?: string;
  }): Promise<RouteSegment | null> => {
    try {
      const result = await updateSegmentFieldsMutation({
        variables: { input },
      });
      toastSuccess('toasts:success.updated');
      return result.data?.updateRouteSegment ?? null;
    } catch (err) {
      if (isOfflineQueuedError(err)) {
        // Sin red no vuelve nada del servidor, así que la fila seguiría
        // enseñando lo viejo hasta que sincronice la cola — y el operario
        // corregiría el carril, vería el de antes y lo volvería a corregir. Se
        // escribe el cambio en la entidad normalizada: es lo mínimo que hace
        // falta, no un `optimisticResponse` con el fragment entero, que
        // obligaría a arrastrar campos que aquí no tenemos.
        client.cache.modify({
          id: client.cache.identify({
            __typename: 'RouteSegment',
            id: input.id,
          }),
          fields: {
            direction: (prev) => input.direction ?? prev,
            directionName: (prev) => input.directionName ?? prev,
            lane: (prev) => input.lane ?? prev,
            pauseCauseDetail: (prev) => input.pauseCauseDetail ?? prev,
          },
        });
        toastQueued();
        return null;
      }
      toastError(err);
      throw err;
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // CROSS-LINKS (V4 D2 — Order self-ref M:N)
  //
  // El backend escribe en `Order.segmentLinkedBasicActionOrderIDs` o
  // `Order.segmentLinkedExternalOrderIDs` + recíprocos en `$transaction`.
  // El segmento devuelto incluye sus orderIDs actualizadas.
  // Llamadas idempotentes (no fallan si el link ya existe / no existe).
  // ═══════════════════════════════════════════════════════════════════

  const linkOrderToSegment = async (
    segmentId: string,
    orderId: string
  ): Promise<RouteSegment | null> => {
    try {
      const result = await linkOrderMutation({
        variables: { input: { segmentId, orderId } },
      });
      toastSuccess('toasts:success.updated');
      return result.data?.linkOrderToSegment ?? null;
    } catch (err) {
      if (isOfflineQueuedError(err)) {
        toastQueued();
        return null;
      }
      toastError(err);
      throw err;
    }
  };

  /** Vincula una OT genérica/campaña al recorrido (N:M, idempotente). */
  const linkOrderToSurveillanceRoute = async (
    surveillanceRouteId: string,
    orderId: string
  ): Promise<SurveillanceRoute | null> => {
    try {
      const result = await linkRouteOrderMutation({
        variables: { input: { surveillanceRouteId, orderId } },
      });
      toastSuccess('toasts:success.updated');
      return result.data?.linkOrderToSurveillanceRoute ?? null;
    } catch (err) {
      if (isOfflineQueuedError(err)) {
        toastQueued();
        return null;
      }
      toastError(err);
      throw err;
    }
  };

  /** Quita el vínculo recorrido ↔ OT (no toca los vínculos de tramo). */
  const unlinkOrderFromSurveillanceRoute = async (
    surveillanceRouteId: string,
    orderId: string
  ): Promise<SurveillanceRoute | null> => {
    try {
      const result = await unlinkRouteOrderMutation({
        variables: { input: { surveillanceRouteId, orderId } },
      });
      toastSuccess('toasts:success.updated');
      return result.data?.unlinkOrderFromSurveillanceRoute ?? null;
    } catch (err) {
      if (isOfflineQueuedError(err)) {
        toastQueued();
        return null;
      }
      toastError(err);
      throw err;
    }
  };

  const linkExternalOrderToSegment = async (
    segmentId: string,
    orderId: string
  ): Promise<RouteSegment | null> => {
    try {
      const result = await linkExternalMutation({
        variables: { input: { segmentId, orderId } },
      });
      toastSuccess('toasts:success.updated');
      return result.data?.linkExternalOrderToSegment ?? null;
    } catch (err) {
      if (isOfflineQueuedError(err)) {
        toastQueued();
        return null;
      }
      toastError(err);
      throw err;
    }
  };

  const unlinkOrderFromSegment = async (
    segmentId: string,
    orderId: string,
    /**
     * `silent`: sin aviso de éxito ni de «en cola» — para cuando el desvínculo
     * es el primer paso de otro gesto que ya avisa (2026-09-24: «Editar» un
     * aviso desvincula y vincula, y salían dos «Actualizado correctamente»).
     * Los errores se avisan siempre.
     */
    options: { silent?: boolean } = {}
  ): Promise<RouteSegment | null> => {
    try {
      const result = await unlinkOrderMutation({
        variables: { input: { segmentId, orderId } },
      });
      if (!options.silent) toastSuccess('toasts:success.updated');
      return result.data?.unlinkOrderFromSegment ?? null;
    } catch (err) {
      if (isOfflineQueuedError(err)) {
        if (!options.silent) toastQueued();
        return null;
      }
      toastError(err);
      throw err;
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // GPS TRACKING — silent (no toasts, high frequency)
  // ═══════════════════════════════════════════════════════════════════

  const addGpsPointsBatch = async (
    surveillanceRouteId: string,
    points: GpsPointInput[]
  ): Promise<SurveillanceRoute | null> => {
    if (points.length === 0) return null;
    try {
      const result = await addGpsPointsBatchMutation({
        variables: { input: { surveillanceRouteId, points } },
      });
      return result.data?.addGpsPointsBatch ?? null;
    } catch (err) {
      // Encolado offline = entrega diferida (la cola lo reproduce al
      // reconectar) → NO relanzar: si el caller re-bufferizara, los puntos
      // llegarían por duplicado tras el replay.
      if (isOfflineQueuedError(err)) return null;
      // Fallo REAL (server caído / transitorio agotado): relanzar para que
      // el tracking re-bufferice. Fix auditoría 2026-07-21: antes se tragaba
      // TODOS los errores (return null) y el ".catch → re-queue" de
      // useSurveillanceRouteGpsTracking era código muerto — los puntos del
      // flush fallido se PERDÍAN en silencio. Sigue sin toast (op de fondo,
      // alta frecuencia, by design).
      console.warn('[SR-GPS] addGpsPointsBatch failed:', err);
      throw err;
    }
  };

  return {
    // Route control (Timer iteration 2026-04-15: only start + finish)
    startRoute,
    finishRoute,
    isStarting,
    isFinishing,

    // Timer (RouteSegment) operations
    createSegment,
    deleteSegment,
    finishSegment,
    updateSegmentPauseCause,
    correctSegment,
    updateSegmentFields,
    isCreatingSegment,
    isDeletingSegment,
    isFinishingSegment,
    isUpdatingPauseCause,
    isCorrectingSegment,
    splitSegment,
    undoSegmentSplit,
    isSplittingSegment,
    transferSegment,
    isTransferringSegment,
    moveSegments,
    isMovingSegments,
    isUndoingSegmentSplit,

    // Cross-links (V4 D2 — Order self-ref M:N)
    linkOrderToSegment,
    linkExternalOrderToSegment,
    unlinkOrderFromSegment,
    linkOrderToSurveillanceRoute,
    unlinkOrderFromSurveillanceRoute,
    isLinkingOrder,
    isLinkingExternal,
    isUnlinking,

    // GPS tracking
    addGpsPointsBatch,
  };
}

export default useSurveillanceRoutes;
